/* eslint-disable @reatom/unit-naming-rule */
import { action, reaction } from '@reatom/framework'
import { match, P } from 'ts-pattern'

import { AsyncStateN } from '@blade/std-lib'
import {
  AuthState,
  authStateAtom,
  redirectToAuthEvent,
} from '@root/entities/auth'
import {
  loginWithGoogleDeeplinkEvent,
  registerWithGoogleDeeplinkEvent,
} from '@root/features/auth'
import { loginModel, otpRequiredEvent } from '@root/features/auth/authenticate'
import {
  loginWithTelegramDeeplinkEvent,
  registerWithTelegramDeeplinkEvent,
} from '@root/features/auth/telegram/atoms'
import {
  loginWithTwitterDeeplinkEvent,
  registerWithTwitterDeeplinkEvent,
} from '@root/features/auth/twitter/atoms'
import { AuthRoute } from '@root/features/routing/model'
import {
  initializeSecureStorageStateAction,
  storageSessionAtom,
} from '@root/features/secure-storage/atoms'
import { StorageSession } from '@root/features/secure-storage/model'
import { ApiError, apiErrorEvent } from '@root/shared/api'
import { isTauriOrBrowserLocalAtom } from '@root/shared/core'
import { routerActions } from '@root/shared/lib/react-router'

import { InitializationState } from '../../model'

import {
  initializationStateAtom,
  isInitializationReadyAtom,
  isInitializationReadyOrWebsiteAtom,
  isWebsiteAtom,
} from './reatom-initialization-state'
import { meStateAtom } from './reatom-me'

import type { Ctx } from '@reatom/framework'

export const authStateReaction = reaction((ctx) => {
  const state = ctx.spy(authStateAtom)

  if (AuthState.isInitial(state)) {
    return
  }

  if (AuthState.isLoggedIn(state)) {
    initializeSecureStorageStateAction(ctx, ctx.get(state.user).delugeId)
  }

  if (AuthState.isSessionExpired(state)) {
    redirectToAuthEvent(ctx)
  }
}, 'authStateReaction')

export const meRequestStateReaction = reaction((ctx) => {
  const meState = ctx.spy(meStateAtom)

  if (
    AsyncStateN.isError(meState) &&
    ApiError.isUnauthorizedOrExpired(meState.error)
  ) {
    routerActions.navigate(ctx, AuthRoute.build())
    initializationStateAtom(ctx, new InitializationState.Initial())
  }
}, 'meRequestStateReaction')

export const secureStorageStateReaction = reaction(
  (ctx, onReady: (ctx: Ctx) => void) => {
    const session = ctx.spy(storageSessionAtom)

    const authState = ctx.spy(authStateAtom)

    if (!AuthState.isLoggedIn(authState) || !session) {
      return
    }

    match(session)
      .with(P.instanceOf(StorageSession.NotInitialized), () => {
        // allow user to get into the app without init
        if (session.walletsCount === 0) {
          initializationStateAtom(ctx, new InitializationState.Ready())
          onReady(ctx)
          return
        }

        initializationStateAtom(
          ctx,
          new InitializationState.InitializeStorage(),
        )
      })
      .with(P.instanceOf(StorageSession.Locked), () => {
        initializationStateAtom(ctx, new InitializationState.UnlockStorage())
      })
      .with(P.instanceOf(StorageSession.Unlocked), () => {
        const currentInitializationState = ctx.get(initializationStateAtom)
        if (currentInitializationState instanceof InitializationState.Ready) {
          return
        }
        initializationStateAtom(ctx, new InitializationState.Ready())
        onReady(ctx)
      })
      .exhaustive()
  },
  'secureStorageStateReaction',
)

export const initializeInitializationDeepLinksAction = action(() => {
  // Login through socials will be handled in login atoms
  loginWithTelegramDeeplinkEvent.onCall((ctx, payload) => {
    if (ctx.get(isWebsiteAtom)) {
      return
    }
    initializationStateAtom(
      ctx,
      new InitializationState.Login({
        loginMethod: {
          type: 'telegram',
          code: payload.code,
          token: payload.token,
        },
      }),
    )
    loginModel.loginAction(ctx, {
      code: payload.code,
      token: payload.token,
      otp: undefined,
      type: 'telegram',
    })
  })
  registerWithTelegramDeeplinkEvent.onCall(
    (ctx, { code, token, referralCode }) => {
      if (ctx.get(isWebsiteAtom)) {
        return
      }
      initializationStateAtom(
        ctx,
        new InitializationState.Register({
          registrationMethod: {
            type: 'telegram',
            code: code,
            token: token,
            referralCode,
          },
        }),
      )
    },
  )
  loginWithGoogleDeeplinkEvent.onCall((ctx, payload) => {
    if (ctx.get(isWebsiteAtom)) {
      return
    }
    initializationStateAtom(
      ctx,
      new InitializationState.Login({
        loginMethod: {
          type: 'google',
          code: payload.code,
        },
      }),
    )
    loginModel.loginAction(ctx, {
      code: payload.code,
      otp: undefined,
      type: 'google',
    })
  })
  registerWithGoogleDeeplinkEvent.onCall((ctx, { code, referralCode }) => {
    if (ctx.get(isWebsiteAtom)) {
      return
    }
    initializationStateAtom(
      ctx,
      new InitializationState.Register({
        registrationMethod: {
          type: 'google',
          code,
          referralCode,
        },
      }),
    )
  })
  loginWithTwitterDeeplinkEvent.onCall((ctx, payload) => {
    if (ctx.get(isWebsiteAtom)) {
      return
    }
    initializationStateAtom(
      ctx,
      new InitializationState.Login({
        loginMethod: {
          type: 'twitter',
          code: payload.code,
        },
      }),
    )
    loginModel.loginAction(ctx, {
      code: payload.code,
      otp: undefined,
      type: 'twitter',
    })
  })
  registerWithTwitterDeeplinkEvent.onCall((ctx, { code, referralCode }) => {
    if (ctx.get(isWebsiteAtom)) {
      return
    }
    initializationStateAtom(
      ctx,
      new InitializationState.Register({
        registrationMethod: {
          type: 'twitter',
          code,
          referralCode,
        },
      }),
    )
  })

  apiErrorEvent.onCall((ctx, _, [error]) => {
    if (ctx.get(isWebsiteAtom)) {
      return
    }
    if (ApiError.isUnauthorizedOrExpired(error)) {
      redirectToAuthEvent(ctx)
    }
  })

  otpRequiredEvent.onCall((ctx, loginPayload) => {
    initializationStateAtom(ctx, new InitializationState.Otp({ loginPayload }))
  })
})

redirectToAuthEvent.onCall((ctx, _) => {
  // In a browser the front door is a screen, not a route: a visitor who is
  // not signed in gets it from the access gate wherever they stand. It used
  // to offer the desktop app here, which is not a way to sign in.
  if (ctx.get(isWebsiteAtom) || !ctx.get(isTauriOrBrowserLocalAtom)) {
    return
  }

  routerActions.navigate(ctx, AuthRoute.build())
  initializationStateAtom(ctx, new InitializationState.Initial())
})

/**
 * Calls {cb} once when initialization is ready. Only for app
 */
export const onInitializationReadyAction = action((ctx, cb: () => void) => {
  const isReady = ctx.get(isInitializationReadyAtom)
  if (isReady) {
    cb()
    return
  }

  const unsubscribe = ctx.subscribe(isInitializationReadyAtom, (isReady) => {
    if (isReady) {
      cb()
      unsubscribe()
    }
  })

  return unsubscribe
}, 'onInitializationReadyAction')

/**
 * Calls {cb} once when initialization is ready. Only for app
 */
export const onInitializationReadyOrWebsiteAction = action(
  (ctx, cb: () => void) => {
    const isReadyOrWebsite = ctx.get(isInitializationReadyOrWebsiteAtom)
    if (isReadyOrWebsite) {
      cb()
      return
    }

    const unsubscribe = ctx.subscribe(
      isInitializationReadyOrWebsiteAtom,
      (isReadyOrWebsite) => {
        if (isReadyOrWebsite) {
          cb()
          unsubscribe()
        }
      },
    )

    return unsubscribe
  },
  'onInitializationReadyOrWebsiteAction',
)
